import React, { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';

import { Button } from '~components';
import { useToast } from '~libs/toast';
import { MAX_SIGNATURE_SIZE_BYTES } from '~constants/common';

import { FormField } from '~forms/types';

interface ComponentProps {
  field: FormField;
  value: string;
  onChange: (fieldId: string, newValue: string) => void;
  error?: string;
  /**
   * Id of the <label> the parent actually rendered, or undefined when it rendered none (an empty
   * label, or one that resolves to nothing because it was only an unresolved merge token). The
   * parent owns this because only it knows the resolved label text. Pointing aria-labelledby at an
   * id that is not in the document leaves the group with no accessible name at all, which is worse
   * than the 'Signature' fallback, so the two must never disagree.
   */
  labelId?: string;
}

// Fixed capture surface, not the library's "100%" default. A responsive surface would make the
// exported raster's pixel dimensions (and therefore its byte size) depend on the viewport. On narrow
// viewports the wrapper scrolls horizontally rather than CSS-scaling the surface, because scaling a
// pointer-driven drawing surface risks misaligning pointer coordinates from the rendered strokes.
const SIGNATURE_WIDTH_PX = 600;
const SIGNATURE_HEIGHT_PX = 200;

// Decoded byte size of a base64 data URI, without allocating a Buffer/Blob just to measure it.
function getDataUriByteSize(dataUri: string): number {
  const commaIndex = dataUri.indexOf(',');
  const base64 = commaIndex >= 0 ? dataUri.slice(commaIndex + 1) : dataUri;
  let padding = 0;
  if (base64.endsWith('==')) padding = 2;
  else if (base64.endsWith('=')) padding = 1;
  return (base64.length * 3) / 4 - padding;
}

/**
 * Signature capture control for the Custom Forms fill renderer. Wraps `react-sketch-canvas`, whose
 * `exportImage` is asynchronous, so the value is exported on stroke end (via the library's `onStroke`
 * callback) rather than at submit. `FormFillPage` reads `responses` synchronously for both required-field
 * validation and the submitted snapshot, so `onChange` must always be called with the current signature
 * as soon as a stroke completes, not lazily at submit time.
 *
 * Two counters guard the async export and MUST stay per-instance `useRef` values: a form can carry more
 * than one signature field (e.g. client plus witness), each mounting its own `SignatureField` instance.
 * Module-level counters would let two signature fields on the same form clobber each other.
 */
// `value` is accepted (not read) to keep the renderer contract identical to every other field type.
// react-sketch-canvas owns the drawn strokes as internal state; the emitted string is a one-way derived
// export, and there is no supported way to reload a previously captured raster back onto the surface.
const SignatureField: React.FC<ComponentProps> = ({ field, value: _value, onChange, error, labelId }) => {
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const { showToast } = useToast();

  // Counts completed strokes so an untouched surface is never exported: `exportImage` on an empty
  // surface resolves to a valid, non-empty blank PNG, which would silently satisfy a required-field check.
  const strokeCountRef = useRef(0);
  // Monotonic sequence number for the stale-export race. Incremented whenever an export starts (so the
  // last-started export wins) or whenever a synchronous change (Clear, Undo-to-zero) must invalidate any
  // export already in flight.
  const exportSeqRef = useRef(0);

  const inputId = `fill-field-signature-${field.id}`;
  const errorId = `${inputId}-error`;
  const requiredId = `${inputId}-required`;
  // Matches how every other editable branch in FillFieldRenderer computes it.
  const isRequired = !!field.required && !field.read_only;

  // Sibling field types announce mandatory-ness with `aria-required`, but they are real form widgets.
  // The capture surface is not: it is a `role="group"`, and `aria-required` is not a permitted
  // attribute on that role (axe fails it as aria-allowed-attr). Carry the same information in the
  // group's accessible description instead, which is valid on `group` and reaches the same users.
  // The visible `*` cannot do this job: it is aria-hidden, exactly as it is on every sibling field.
  const describedBy = [isRequired ? requiredId : null, error ? errorId : null].filter(Boolean).join(' ');

  const exportCurrent = useCallback(() => {
    if (strokeCountRef.current <= 0) return;
    const seq = ++exportSeqRef.current;
    canvasRef.current
      ?.exportImage('png')
      .then((dataUri) => {
        // A later export has already started (or the surface was cleared/undone to empty), so discard.
        if (seq !== exportSeqRef.current) return;
        if (getDataUriByteSize(dataUri) > MAX_SIGNATURE_SIZE_BYTES) {
          // Wipe the surface as well as the value. Leaving the drawing on screen while the value is
          // discarded makes the field look signed when it is not, and every further stroke would
          // re-export an even larger image and be rejected again.
          canvasRef.current?.clearCanvas();
          strokeCountRef.current = 0;
          showToast('danger', 'Signature is too large. Please simplify your signature and try again.');
          onChange(field.id, '');
          return;
        }
        onChange(field.id, dataUri);
      })
      .catch(() => {
        // exportImage rejects only if the surface is gone mid-export (e.g. unmount), so there is nothing to persist.
      });
  }, [field.id, onChange, showToast]);

  const handleStroke = useCallback(() => {
    strokeCountRef.current += 1;
    exportCurrent();
  }, [exportCurrent]);

  const handleClear = useCallback(() => {
    canvasRef.current?.clearCanvas();
    strokeCountRef.current = 0;
    exportSeqRef.current += 1; // invalidate any export still in flight
    onChange(field.id, '');
  }, [field.id, onChange]);

  const handleUndo = useCallback(() => {
    // `undo()` is a setState inside the library, but `exportImage` clones the live SVG node
    // synchronously before it awaits anything. Calling them back to back would serialise the
    // pre-undo surface, silently keeping the stroke the user just removed in the exported PNG.
    // flushSync commits the removal to the DOM first, so the export below reads the corrected surface.
    flushSync(() => {
      canvasRef.current?.undo();
    });
    strokeCountRef.current = Math.max(0, strokeCountRef.current - 1);
    if (strokeCountRef.current === 0) {
      exportSeqRef.current += 1; // invalidate any export still in flight
      onChange(field.id, '');
    } else {
      exportCurrent();
    }
  }, [field.id, onChange, exportCurrent]);

  return (
    <div className="form-fill-signature">
      <div
        className="form-fill-signature-scroll"
        role="group"
        aria-labelledby={labelId}
        aria-label={labelId ? undefined : 'Signature'}
        aria-invalid={!!error}
        aria-describedby={describedBy || undefined}
      >
        {isRequired && (
          <span id={requiredId} className="visually-hidden">
            Required
          </span>
        )}
        <div className="form-fill-signature-surface" style={{ width: SIGNATURE_WIDTH_PX }}>
          <ReactSketchCanvas
            ref={canvasRef}
            width={`${SIGNATURE_WIDTH_PX}px`}
            height={`${SIGNATURE_HEIGHT_PX}px`}
            strokeWidth={2}
            strokeColor="black"
            canvasColor="white"
            allowOnlyPointerType="all"
            onStroke={handleStroke}
          />
        </div>
      </div>
      <div className="form-fill-signature-actions">
        <Button
          type="button"
          color="info"
          outline
          size="sm"
          onClick={handleClear}
          aria-label="Clear signature"
          dataCy={`signature-clear-btn-${field.id}`}
        >
          Clear
        </Button>
        <Button
          type="button"
          color="info"
          outline
          size="sm"
          onClick={handleUndo}
          aria-label="Undo last signature stroke"
          dataCy={`signature-undo-btn-${field.id}`}
        >
          Undo
        </Button>
      </div>
      {error && (
        <div id={errorId} className="form-fill-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default SignatureField;
